import { useQuery } from '@tanstack/react-query';
import { useId, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { api } from '../lib/api';
import { cn } from './ui';

export interface PersonOption {
  id: string;
  displayName: string;
}

/**
 * Personenauswahl mit Schnellsuche.
 *
 * Eine Liste aller Personen ist ab etwa dreissig Eintraegen unbrauchbar: man
 * scrollt und sucht mit den Augen. Hier wird stattdessen getippt, die Treffer
 * schrumpfen mit jedem Buchstaben, und das Gewaehlte steht als abnehmbares
 * Etikett vor dem Eingabefeld - so ist auf einen Blick zu sehen, wer schon
 * drin ist, ohne die Liste durchzugehen.
 */

/** Vergleichstext: klein geschrieben, Umlaute in beiden Schreibweisen auffindbar. */
function suchtext(text: string): string {
  return text
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss');
}

interface ComboboxProps {
  value: string[];
  onChange: (ids: string[]) => void;
  /** Ohne Vorgabe werden die Personen des Vereins geladen. */
  options?: PersonOption[];
  id?: string;
  /** Beschriftung, wenn das Feld ohne `Field` auskommt. */
  ariaLabel?: string;
  multiple?: boolean;
  placeholder?: string;
  /** Zusatz hinter dem Namen, etwa "(ich)". */
  decorate?: (person: PersonOption) => ReactNode;
  /** Diese Person steht in der Trefferliste ganz oben. */
  preferId?: string;
  disabled?: boolean;
  emptyText?: string;
}

function PersonCombobox({
  value,
  onChange,
  options,
  id,
  ariaLabel,
  multiple = true,
  placeholder = 'Name eingeben …',
  decorate,
  preferId,
  disabled = false,
  emptyText = 'Keine Personen verfügbar.',
}: ComboboxProps): JSX.Element {
  const listId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [query, setQuery] = useState('');
  const [offen, setOffen] = useState(false);
  const [aktiv, setAktiv] = useState(0);

  const geladen = useQuery({
    queryKey: ['persons', 'options'],
    queryFn: () => api.get<PersonOption[]>('/api/persons/options'),
    enabled: options === undefined,
    staleTime: 60_000,
  });

  const alle = options ?? geladen.data ?? [];

  const sortiert = useMemo(() => {
    const liste = [...alle].sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'));
    // Bewusst zwei Durchlaeufe statt eines Vergleichs: ein Komparator, der
    // fast ueberall 0 liefert, sortiert nicht zuverlaessig.
    if (!preferId) return liste;
    return [
      ...liste.filter((p) => p.id === preferId),
      ...liste.filter((p) => p.id !== preferId),
    ];
  }, [alle, preferId]);

  /** Auswahlreihenfolge beibehalten - so bleibt sichtbar, was zuletzt kam. */
  const gewaehlt = useMemo(
    () => value.map((id) => sortiert.find((p) => p.id === id) ?? { id, displayName: 'Unbekannt' }),
    [value, sortiert],
  );

  const treffer = useMemo(() => {
    const gesucht = suchtext(query.trim());
    return sortiert.filter(
      (p) => !value.includes(p.id) && (gesucht === '' || suchtext(p.displayName).includes(gesucht)),
    );
  }, [sortiert, value, query]);

  const beschriftung = (person: PersonOption): ReactNode => decorate?.(person) ?? person.displayName;

  const waehlen = (personId: string): void => {
    onChange(multiple ? [...value, personId] : [personId]);
    setQuery('');
    setAktiv(0);
    if (!multiple) setOffen(false);
    inputRef.current?.focus();
  };

  const entfernen = (personId: string): void => {
    onChange(value.filter((v) => v !== personId));
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setOffen(true);
      if (treffer.length === 0) return;
      const richtung = event.key === 'ArrowDown' ? 1 : -1;
      setAktiv((alt) => (alt + richtung + treffer.length) % treffer.length);
      return;
    }
    if (event.key === 'Enter') {
      const gewaehlter = treffer[aktiv];
      if (offen && gewaehlter) {
        // Sonst schickt der umgebende Dialog das Formular ab.
        event.preventDefault();
        waehlen(gewaehlter.id);
      }
      return;
    }
    if (event.key === 'Escape' && offen) {
      // Erst die Liste schliessen, nicht gleich den ganzen Dialog.
      event.preventDefault();
      event.stopPropagation();
      setOffen(false);
      return;
    }
    if (event.key === 'Backspace' && query === '' && value.length > 0) {
      entfernen(value[value.length - 1]!);
    }
  };

  return (
    <div
      className="relative"
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOffen(false);
      }}
    >
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-300 bg-white px-2 py-1.5 shadow-sm transition-colors',
          'focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-500/30',
          disabled ? 'bg-slate-100' : 'hover:border-slate-400',
        )}
      >
        {gewaehlt.map((person) => (
          <span
            key={person.id}
            className="inline-flex max-w-full items-center gap-1 rounded-full bg-brand-50 py-1 pl-2.5 pr-1 text-sm font-medium text-brand-800 ring-1 ring-inset ring-brand-200"
          >
            <span className="truncate">{beschriftung(person)}</span>
            <button
              type="button"
              disabled={disabled}
              aria-label={`${person.displayName} entfernen`}
              onClick={() => entfernen(person.id)}
              className="rounded-full p-0.5 text-brand-700 hover:bg-brand-100 hover:text-brand-900 disabled:opacity-50"
            >
              <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                <path d="M6.3 5A1 1 0 004.9 6.4L8.6 10l-3.7 3.6a1 1 0 101.4 1.4l3.7-3.6 3.6 3.6a1 1 0 001.4-1.4L11.4 10l3.6-3.6a1 1 0 00-1.4-1.4L10 8.6 6.3 5z" />
              </svg>
            </button>
          </span>
        ))}
        <input
          id={id}
          ref={inputRef}
          type="text"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={offen}
          aria-controls={listId}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={query}
          placeholder={!multiple && gewaehlt.length > 0 ? 'Andere Person suchen …' : placeholder}
          onChange={(event) => {
            setQuery(event.target.value);
            setAktiv(0);
            setOffen(true);
          }}
          onFocus={() => setOffen(true)}
          onKeyDown={onKeyDown}
          className="min-w-[8rem] flex-1 border-0 bg-transparent px-1 py-1 text-base text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-0 disabled:bg-transparent"
        />
      </div>

      {offen && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-30 mt-1 max-h-56 w-full overflow-y-auto rounded-xl border border-slate-200 bg-white py-1 shadow-lg"
        >
          {/*
           * Die Eintraege sind bewusst keine Schaltflaechen: bedient wird die
           * Liste ueber das Eingabefeld (Pfeiltasten, Eingabetaste), so will
           * es das Muster fuer Kombinationsfelder.
           */}
          {treffer.map((person, index) => (
            <li
              key={person.id}
              role="option"
              aria-selected={index === aktiv}
              // Der Klick darf den Fokus nicht vorher wegnehmen, sonst
              // schliesst der Blur die Liste vor dem Klick.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => waehlen(person.id)}
              onMouseEnter={() => setAktiv(index)}
              className={cn(
                'cursor-pointer px-3 py-2 text-sm',
                index === aktiv ? 'bg-brand-50 text-brand-900' : 'text-slate-700',
              )}
            >
              {beschriftung(person)}
            </li>
          ))}
          {treffer.length === 0 && (
            <li className="px-3 py-2 text-sm text-slate-500">
              {sortiert.length === 0
                ? emptyText
                : query.trim()
                  ? `Keine Person zu „${query.trim()}“.`
                  : 'Alle Personen sind bereits gewählt.'}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

/** Mehrfachauswahl: gewaehlte Personen stehen als Etiketten im Feld. */
export function PersonPicker(props: Omit<ComboboxProps, 'multiple'>): JSX.Element {
  return <PersonCombobox {...props} multiple />;
}

/** Einfachauswahl als Ersatz fuer ein langes Auswahlfeld. */
export function PersonSelect({
  value,
  onChange,
  ...rest
}: Omit<ComboboxProps, 'multiple' | 'value' | 'onChange'> & {
  value: string | null;
  onChange: (id: string | null) => void;
}): JSX.Element {
  return (
    <PersonCombobox
      {...rest}
      multiple={false}
      value={value ? [value] : []}
      onChange={(ids) => onChange(ids[0] ?? null)}
    />
  );
}
